import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
// Domain
import * as contentTranslationUseCases from './contentTranslationUseCases';
import useContent from '../content/useContent';

/**
 * @typedef {Array<{label: string,
 * value: ContentTranslationStatus
 * }>} ContentTranslationStatusOptions
 */

/**
 * @callback SwitchFormContentTranslation
 * @param {Content} content
 * @param {string} newLocaleSelected
 * @param {string} defaultLocale
 * @param {Function} formValuesReset
 * @param {boolean} isFormDirty
 * @returns {void}
 */

/**
 * @callback SwitchContentTranslationVersion Switch the content translation version
 * @param {ContentTranslationDataVersion} version
 * @param {Function} formValuesReset
 * @returns {void}
 */

/**
 * @callback SaveContentTranslation Save the content translation
 * @param {any} values
 * @param {ContentTranslationStatus} statusToSave
 * @param {ContentTranslationStatus} currentTranslationStatus
 * @param {Function} triggerFormValidation
 * @param {Function} saveFormCallback
 * @param {Function} formSanitizer
 * @returns {Promise<void>}
 */

/**
 * @callback PublishOrWithdrawContentTranslation Publish or withdraw the content translation
 * @param {Function} publishCallback
 * @param {Function} withdrawCallback
 * @param {ContentTranslationStatus} statusToSave
 * @param {ContentTranslationStatus} currentTranslationStatus
 * @returns {void}
 */

/**
 *
 * @returns {{ cleanDataFromAPI: contentTranslationUseCases.CleanDataFromAPI,
 * getContentTranslationStatusUIConfig: contentTranslationUseCases
 *  .getContentTranslationStatusUIConfig,
 * switchFormContentTranslation: SwitchFormContentTranslation,
 * switchContentTranslationVersion: SwitchContentTranslationVersion,
 * saveContentTranslation: SaveContentTranslation,
 * publishOrWithdrawContentTranslation: PublishOrWithdrawContentTranslation,
 * contentTranslationStatusOptions: ContentTranslationStatusOptions,
 * TRANSLATIONS_STATUSES: contentTranslationUseCases.ContentTranslationStatusEnum }}
 */
const useContentTranslation = () => {
  // Hooks
  const [, setSearchParams] = useSearchParams();
  const { getTranslationFromContent } = useContent();
  const { t } = useTranslation();

  // Public variables
  /** @type {ContentTranslationStatusOptions} */
  const contentTranslationStatusOptions = Object
    .keys(contentTranslationUseCases.TRANSLATIONS_STATUSES)
    .map((key) => ({
      value: contentTranslationUseCases.TRANSLATIONS_STATUSES[key],
      label: t(`cortizol.contentTranslation.${contentTranslationUseCases.TRANSLATIONS_STATUSES[key]}`),
    }));

  // Public methods
  /** @type {SwitchFormContentTranslation} */
  const switchFormContentTranslation = (
    content,
    newLocaleSelected,
    defaultLocale,
    formValuesReset,
    isFormDirty,
  ) => {
    const switchFormContent = () => {
      setSearchParams((params) => {
        params.set('locale', newLocaleSelected);
        return params;
      });

      const newContentTranslation = getTranslationFromContent(content, newLocaleSelected);
      const defaultLocaleContentTranslation = getTranslationFromContent(content, defaultLocale);
      const cleanedContentTranslation = contentTranslationUseCases
        .cleanDataFromAPI(newContentTranslation || defaultLocaleContentTranslation).data;

      formValuesReset(cleanedContentTranslation);
    };

    if (isFormDirty) {
      // eslint-disable-next-line no-alert
      const confirmed = window.confirm(t('cortizol.contentTypeForm.language.changeWarning'));
      if (confirmed) switchFormContent();
    } else {
      switchFormContent();
    }
  };
  /** @type {SwitchContentTranslationVersion} */
  const switchContentTranslationVersion = (version, formValuesReset) => {
    const { data } = version;
    // eslint-disable-next-line no-alert
    const confirmed = window.confirm(t('cortizol.contentTypeForm.history.changeWarning'));
    const dataNotEmpty = Object.keys(data).length > 0;

    if (confirmed && dataNotEmpty) {
      const cleanedContentTranslation = contentTranslationUseCases
        .cleanDataFromAPI(/** @type {ContentTranslation} */({ data: version.data })).data;

      formValuesReset(cleanedContentTranslation);
    }
  };
  /** @type {SaveContentTranslation} */
  const saveContentTranslation = async (
    values,
    statusToSave,
    currentTranslationStatus,
    triggerFormValidation,
    saveFormCallback,
    formSanitizer,
  ) => {
    const sanitizedValues = await formSanitizer(
      values,
      {
        customSanitation: contentTranslationUseCases.CmsApiSanitation,
      },
    );
    const isSameStatusTargeted = statusToSave === currentTranslationStatus;
    let confirmation = true;

    if (!isSameStatusTargeted) {
      // eslint-disable-next-line no-alert
      confirmation = window.confirm(
        t('cortizol.contentTranslation.saveWarning', { statusToSave, currentTranslationStatus }),
      );
    }

    if (confirmation
       && statusToSave === contentTranslationUseCases.TRANSLATIONS_STATUSES.draft
    ) {
      return saveFormCallback(sanitizedValues);
    }

    if (confirmation
       && statusToSave === contentTranslationUseCases.TRANSLATIONS_STATUSES.published
    ) {
      const isFormValid = await triggerFormValidation();
      if (isFormValid) {
        return saveFormCallback(sanitizedValues);
      }
    }

    return undefined;
  };
  /** @type {PublishOrWithdrawContentTranslation} */
  const publishOrWithdrawContentTranslation = (
    publishCallback,
    withdrawCallback,
    statusToSave,
    currentTranslationStatus,
  ) => {
    if (statusToSave !== currentTranslationStatus) {
      if (statusToSave === contentTranslationUseCases.TRANSLATIONS_STATUSES.published) {
        publishCallback();
      } else {
        withdrawCallback();
      }
    }
  };

  return ({
    cleanDataFromAPI: contentTranslationUseCases.cleanDataFromAPI,
    getContentTranslationStatusUIConfig: contentTranslationUseCases
      .getContentTranslationStatusUIConfig,
    switchFormContentTranslation,
    switchContentTranslationVersion,
    saveContentTranslation,
    publishOrWithdrawContentTranslation,
    contentTranslationStatusOptions,
    TRANSLATIONS_STATUSES: contentTranslationUseCases.TRANSLATIONS_STATUSES,
  });
};

export default useContentTranslation;
